import React from "react";

import { Box, CircularProgress, Typography, keyframes } from "@mui/material";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Navigate } from "react-router-dom";

import { canAccessPage } from "../../auth/roles";
import { useUserRole } from "../../context/UserRoleContext";
import uiConfig from "../../config/uiConfig";

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.96); }
`;

function ProtectedRoute({ pageKey, children }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { loading, role } = useUserRole();

  // MSAL's own account/interaction state hasn't synced yet on the very first
  // render (it starts at Startup and only flips to None in its own effect),
  // so isAuthenticated can briefly read false even with a valid cached session.
  // Wait it out instead of bouncing to /login on every deep-link reload.
  if (inProgress !== InteractionStatus.None) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        }}
      >
        {/* Logo / brand ring */}
        <Box sx={{ position: "relative", width: 80, height: 80 }}>
          <CircularProgress
            size={80}
            thickness={2}
            sx={{ color: uiConfig.sidebarBackgroundColor, opacity: 0.15, position: "absolute" }}
            variant="determinate"
            value={100}
          />
          <CircularProgress
            size={80}
            thickness={2.5}
            sx={{ color: uiConfig.sidebarBackgroundColor }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: uiConfig.tableAccentGradient,
              width: 52,
              height: 52,
              top: 14,
              left: 14,
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>IT</Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 0.5 }}>
            Verifying access
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>
            Please wait while we set up your workspace…
          </Typography>
        </Box>

        {/* Animated dots */}
        <Box sx={{ display: "flex", gap: 0.8 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: uiConfig.sidebarBackgroundColor,
                animation: `${pulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 3,
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        }}
      >
        {/* Logo / brand ring */}
        <Box sx={{ position: "relative", width: 80, height: 80 }}>
          <CircularProgress
            size={80}
            thickness={2}
            sx={{ color: uiConfig.sidebarBackgroundColor, opacity: 0.15, position: "absolute" }}
            variant="determinate"
            value={100}
          />
          <CircularProgress
            size={80}
            thickness={2.5}
            sx={{ color: uiConfig.sidebarBackgroundColor }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              background: uiConfig.tableAccentGradient,
              width: 52,
              height: 52,
              top: 14,
              left: 14,
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>IT</Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 0.5 }}>
            Verifying access
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>
            Please wait while we set up your workspace…
          </Typography>
        </Box>

        {/* Animated dots */}
        <Box sx={{ display: "flex", gap: 0.8 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: uiConfig.sidebarBackgroundColor,
                animation: `${pulse} 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  if (!canAccessPage(role, pageKey)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default ProtectedRoute;
